import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// GPS Coordinates — reads navigator.geolocation. Three displays:
//   1. Decimal degrees (e.g. 39.962, -84.166)
//   2. Degrees / minutes / seconds (e.g. 39°57'43"N, 84°09'58"W)
//   3. Maps deep link (opens the user's default maps app)
//
// We use watchPosition (not getCurrentPosition) so the reading updates
// as GPS lock improves. Accuracy radius is shown so the user knows when
// to trust the reading.

const toDMS = (deg, isLat) => {
  const d = Math.abs(deg);
  const degrees = Math.floor(d);
  const minutes = Math.floor((d - degrees) * 60);
  const seconds = ((d - degrees) * 60 - minutes) * 60;
  const dir = isLat ? (deg >= 0 ? "N" : "S") : (deg >= 0 ? "E" : "W");
  return `${degrees}°${minutes.toString().padStart(2, "0")}'${seconds.toFixed(1).padStart(4, "0")}"${dir}`;
};

export default function GPSCoordinates() {
  const [state, setState] = useState("idle"); // idle | requesting | ok | denied | unsupported
  const [pos, setPos] = useState(null);
  const [copied, setCopied] = useState("");
  const watchRef = useRef(null);

  const enable = () => {
    if (!navigator.geolocation) { setState("unsupported"); return; }
    setState("requesting");
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => {
        setPos({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
          altitude: p.coords.altitude,
          heading: p.coords.heading,
          speed: p.coords.speed,
          timestamp: p.timestamp,
        });
        setState("ok");
      },
      (err) => {
        if (err.code === 1) setState("denied");
        else if (err.code === 3) setState("denied"); // timeout — treat as denied for UX
        else setState("denied");
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 30000 }
    );
  };

  useEffect(() => () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
  }, []);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(""), 1500);
    } catch {}
  };

  // Accuracy-quality tier
  const accuracyTier = (acc) => {
    if (acc == null) return { label: "—", color: "#9A9DBE" };
    if (acc <= 10) return { label: "Excellent", color: "#5BD99A" };
    if (acc <= 30) return { label: "Good", color: "#F8B84E" };
    if (acc <= 100) return { label: "Fair", color: "#F5A299" };
    return { label: "Weak — move outdoors for better lock", color: "#E0584E" };
  };

  return (
    <ToolsShell title="GPS Coordinates">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Read your current latitude and longitude. Tap to enable location — your position stays on your device.
          </p>
          <button onClick={enable} style={primaryBtn} disabled={state === "requesting"}>
            {state === "requesting" ? "Getting GPS lock…" : "Enable location"}
          </button>
          <SensorNotice state={state === "idle" || state === "requesting" ? "prompt" : state} what="Location" />
        </div>
      )}

      {state === "ok" && pos && (
        <>
          {/* Headline: decimal */}
          <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 14, padding: "20px 22px" }}>
            <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Decimal degrees</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>
              {pos.lat.toFixed(6)}, {pos.lng.toFixed(6)}
            </div>
            <button onClick={() => copy(`${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}`)} style={{ marginTop: 8, background: copied === `${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}` ? "#5BD99A" : "#1C1E2E", color: copied === `${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}` ? "#0A0B14" : TOOLS_ACCENT, border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              {copied === `${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}` ? "✓ Copied" : "Copy"}
            </button>
          </div>

          {/* DMS format */}
          <div style={{ background: "#13141F", border: "1px solid #262839", borderRadius: 12, padding: "14px 18px", marginTop: 10 }}>
            <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Degrees / minutes / seconds</div>
            <div style={{ fontSize: 16, color: "#E8E9F3", fontFamily: "monospace", fontVariantNumeric: "tabular-nums" }}>
              {toDMS(pos.lat, true)}, {toDMS(pos.lng, false)}
            </div>
            <button onClick={() => copy(`${toDMS(pos.lat, true)}, ${toDMS(pos.lng, false)}`)} style={{ marginTop: 8, background: "transparent", color: TOOLS_ACCENT, border: "1px solid #262839", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>
              Copy
            </button>
          </div>

          {/* Maps link */}
          <a
            href={`https://www.google.com/maps?q=${pos.lat},${pos.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "block", marginTop: 12, background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "12px 14px", fontSize: 14, fontWeight: 600, textAlign: "center", textDecoration: "none" }}
          >
            Open in Maps →
          </a>

          {/* Accuracy & extras */}
          <div style={{ marginTop: 14, padding: "12px 16px", background: "#13141F", border: "1px solid #262839", borderRadius: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>Accuracy</div>
              <div style={{ fontSize: 13, color: accuracyTier(pos.accuracy).color, fontWeight: 600 }}>
                {accuracyTier(pos.accuracy).label}
              </div>
            </div>
            <div style={{ fontSize: 14, color: "#E8E9F3", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
              ± {pos.accuracy.toFixed(0)} m
            </div>
          </div>

          {(pos.altitude != null || pos.speed != null) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              {pos.altitude != null && (
                <Mini label="Altitude" value={`${pos.altitude.toFixed(0)} m`} />
              )}
              {pos.speed != null && (
                <Mini label="Speed" value={`${(pos.speed * 3.6).toFixed(1)} km/h`} />
              )}
            </div>
          )}

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            GPS accuracy degrades indoors, in canyons, and near tall buildings. For best lock, stand outdoors with a clear sky view.
          </p>
        </>
      )}
    </ToolsShell>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{ background: "#13141F", border: "1px solid #262839", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#E8E9F3", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
